/**
 * Collection scroll & pagination restoration.
 *
 * When a shopper is on a collection page (PLP) using the "Load more" (click)
 * pagination, clicks "Load more" a few times, then clicks through to a product
 * (PDP) and presses the browser back button, the PLP would normally reload
 * fresh — losing both their scroll position and the extra products they had
 * loaded.
 *
 * This script:
 *   1. Saves the scroll position, the number of products currently rendered,
 *      and the clicked product's URL whenever the shopper leaves the PLP.
 *   2. On returning to the PLP via back/forward navigation, replays the
 *      "Load more" clicks until the same number of products are rendered, then
 *      scrolls back to the product the shopper had clicked.
 *
 * It is a self-contained enhancement that hooks into the theme's existing
 * Ajaxinate "load more" mechanism (the `data-click-to-load` anchor) and the
 * `collection:updated` event the theme dispatches after each AJAX load.
 */
(function () {
  'use strict';

  var SECTION = '[data-section-type="collection"]';
  var CONTAINER = '.collection__infinite-container';
  var TRIGGER_LINK = '.collection__infinite-trigger a[data-click-to-load]';
  var PRODUCT_ITEM = '.product-item';
  var PRODUCT_LINK = '.product-item__image-link';
  var STORAGE_PREFIX = 'plp-restore:';
  var MAX_LOADS = 50; // safety cap so a bad state can never loop forever

  function getSection() {
    return document.querySelector(SECTION);
  }

  // State is keyed by the full collection URL (path + filters/sort), so each
  // filtered view restores independently.
  function storageKey() {
    return STORAGE_PREFIX + window.location.pathname + window.location.search;
  }

  function read() {
    try {
      var raw = sessionStorage.getItem(storageKey());
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function clear() {
    try {
      sessionStorage.removeItem(storageKey());
    } catch (e) {}
  }

  function save(productPath) {
    try {
      var section = getSection();
      if (!section) return;
      var container = section.querySelector(CONTAINER);
      if (!container) return;

      var prev = read();
      var state = {
        count: container.querySelectorAll(PRODUCT_ITEM).length,
        scrollY: window.scrollY || window.pageYOffset || 0,
        // Keep an existing precise target if this save doesn't provide one.
        productPath: productPath || (prev && prev.productPath) || null
      };
      sessionStorage.setItem(storageKey(), JSON.stringify(state));
    } catch (e) {}
  }

  // True when the page was reached via the browser back/forward buttons.
  function isBackForward() {
    try {
      var entries =
        performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (entries && entries.length) {
        return entries[0].type === 'back_forward';
      }
      if (performance.navigation) {
        return performance.navigation.type === 2; // TYPE_BACK_FORWARD
      }
    } catch (e) {}
    return false;
  }

  /* ----------------------------- Saving ----------------------------- */

  // Capture-phase so we record the target before the theme's own click
  // handling (e.g. animated page transitions) runs. Matches any link inside a
  // product card that points at a product (image link, title link, etc.).
  document.addEventListener(
    'click',
    function (evt) {
      var target = evt.target;
      if (!target || !target.closest) return;
      var item = target.closest(PRODUCT_ITEM);
      if (!item || !getSection()) return;
      var link = target.closest('a[href]');
      if (!link || !item.contains(link)) return;
      if (link.pathname.indexOf('/products/') === -1) return;
      save(link.pathname);
    },
    true
  );

  // Safety net for any other way of leaving the page (keyboard nav, etc.).
  window.addEventListener('pagehide', function () {
    if (getSection()) save(null);
  });

  /* ---------------------------- Restoring ---------------------------- */

  // Extract the product handle from a path like
  // "/collections/foo/products/bar" or "/products/bar" -> "bar".
  function productHandle(path) {
    if (!path) return null;
    var match = path.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function findProductLink(productPath) {
    var handle = productHandle(productPath);
    if (!handle) return null;
    var links = document.querySelectorAll(PRODUCT_LINK);
    for (var i = 0; i < links.length; i++) {
      if (productHandle(links[i].pathname) === handle) return links[i];
    }
    return null;
  }

  function scrollToTarget(state) {
    var link = findProductLink(state.productPath);
    if (link) {
      // Centre the product the shopper had clicked in the viewport — robust to
      // small layout differences (lazy images, etc.).
      var item = link.closest(PRODUCT_ITEM) || link;
      var rect = item.getBoundingClientRect();
      var pageTop = window.scrollY || window.pageYOffset || 0;
      var top = rect.top + pageTop - window.innerHeight / 2 + rect.height / 2;
      window.scrollTo(0, Math.max(0, top));
    } else {
      window.scrollTo(0, state.scrollY || 0);
    }
  }

  // Click the "Load more" button repeatedly until we have at least the saved
  // number of products (or there are no more pages to load).
  function loadUntil(targetCount, done) {
    var section = getSection();
    var container = section && section.querySelector(CONTAINER);
    if (!container) {
      done();
      return;
    }

    var attempts = 0;

    function step() {
      var current = container.querySelectorAll(PRODUCT_ITEM).length;
      var trigger = document.querySelector(TRIGGER_LINK);
      if (current >= targetCount || !trigger || attempts >= MAX_LOADS) {
        done();
        return;
      }
      attempts++;
      // The theme fires `collection:updated` (on document) after each AJAX
      // load completes and the new products are in the DOM.
      document.addEventListener('collection:updated', onUpdated, { once: true });
      trigger.click();
    }

    function onUpdated() {
      // Let the appended markup settle for a frame before continuing.
      requestAnimationFrame(step);
    }

    step();
  }

  function restore() {
    var section = getSection();
    if (!section) return;

    // Only restore when the shopper actually came back to this page.
    if (!isBackForward()) {
      clear();
      return;
    }

    var state = read();
    if (!state) return;

    function finish() {
      // Two frames lets the final batch lay out before we measure positions.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          scrollToTarget(state);
          clear();
        });
      });
    }

    var isClickPagination = section.getAttribute('data-pagination-type') === 'click';
    var needsMore =
      isClickPagination &&
      state.count &&
      section.querySelectorAll(PRODUCT_ITEM).length < state.count;

    if (needsMore) {
      loadUntil(state.count, finish);
    } else {
      finish();
    }
  }

  /* ------------------------------ Boot ------------------------------ */

  // If we're going to restore, take over scroll handling so the browser's own
  // restoration doesn't fight us with a jump to the wrong position.
  try {
    if ('scrollRestoration' in history && getSection() && isBackForward() && read()) {
      history.scrollRestoration = 'manual';
    }
  } catch (e) {}

  // When the page is served from the bfcache it is fully intact (products and
  // scroll position included), so there is nothing to rebuild — just discard
  // any saved state.
  window.addEventListener('pageshow', function (evt) {
    if (evt.persisted) clear();
  });

  // Run after the theme has initialised its "load more" handler.
  if (document.readyState === 'complete') {
    restore();
  } else {
    window.addEventListener('load', restore);
  }
})();
